import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { getSelectedPropertyId } from '@/auth/selectedPropertyStore';
import { getAccessToken, setAccessToken } from '@/auth/tokenStore';
import { currentApiLanguage } from '@/i18n';
import {
  createClientOperationId,
  inferResourceFromPath,
  markOfflineMutationConflicted,
  markOfflineMutationFailed,
  markOfflineMutationRetry,
  markOfflineMutationSyncing,
  queueOfflineMutation,
  readOfflineQueue,
  removeOfflineMutation,
  type OfflineMutation,
} from '@/offline/offlineQueue';
import {
  findCachedResourceUpdatedAt,
  readCachedApiEnvelope,
  writeCachedApiEnvelope,
} from '@/offline/offlineApiCache';
import type { ApiEnvelope, ApiErrorPayload, ApiSuccessEnvelope } from '@/api/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  // Content-Type is deliberately NOT set as an instance-level default:
  // buildHeaders() adds `application/json` per JSON request, but a
  // FormData upload must leave Content-Type unset entirely so the
  // browser/axios can generate the multipart boundary itself — an
  // explicit 'application/json' default here would otherwise stick and
  // silently break every file upload.
  headers: { Accept: 'application/json' },
  validateStatus: () => true,
});

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, string[]> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, error: ApiErrorPayload) {
    super(error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
    this.requestId = error.request_id;
  }
}

/**
 * Used for the handful of failure modes that never reach the backend at
 * all (an empty/unparseable body) — there is no server-issued
 * request_id to report, so a client-generated marker keeps ApiError's
 * shape uniform rather than making requestId optional everywhere it's
 * read.
 */
const NO_SERVER_RESPONSE_ERROR: ApiErrorPayload = {
  code: 'NO_SERVER_RESPONSE',
  message: 'The server returned an empty or invalid response.',
  request_id: 'none',
};

const OFFLINE_QUEUED_ERROR: ApiErrorPayload = {
  code: 'OFFLINE_QUEUED',
  message: 'You appear to be offline. This change was saved on this device and will sync when the connection returns.',
  request_id: 'offline',
};

const OFFLINE_UNSUPPORTED_ERROR: ApiErrorPayload = {
  code: 'OFFLINE_UNSUPPORTED',
  message: 'This action needs an internet connection because it includes a file or binary payload.',
  request_id: 'offline',
};

/**
 * Test-only seam for swapping the Axios transport without mocking every
 * module that imports apiRequest. App code should always use the default
 * singleton above.
 */
let activeHttpClient: AxiosInstance = http;

export function setHttpClientForTesting(client: AxiosInstance | null): void {
  activeHttpClient = client ?? http;
}

/**
 * Invoked when a refresh attempt itself fails (refresh token missing,
 * expired, or revoked) — i.e. the session is truly over. AuthContext
 * registers itself here so it can clear user state and redirect to
 * /login without src/api/client.ts needing to import React or
 * AuthContext directly (that would be a circular import).
 */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Internal use: prevents infinite retry loops on a repeated 401. */
  _isRetry?: boolean;
}

interface OfflineRequestMetadata {
  clientOperationId: string;
  resource: string;
  lastKnownUpdatedAt: string | null;
}

/**
 * Shared by apiRequest and apiUpload: sends one request, transparently
 * refreshes-and-retries exactly once on a 401 (never for an auth
 * endpoint itself), then normalizes the response into the discriminated
 * envelope or throws ApiError. Kept as a single implementation so the
 * refresh/retry/error-classification behavior can't drift between the
 * JSON and multipart code paths.
 */
async function sendEnvelopeRequest<TData>(
  path: string,
  method: NonNullable<RequestOptions['method']>,
  data: unknown,
  headers: Record<string, string>,
  isRetry = false,
  offlineMetadata: OfflineRequestMetadata | null = null
): Promise<ApiSuccessEnvelope<TData>> {
  let response: AxiosResponse<ApiEnvelope<TData> | ''>;

  try {
    response = await activeHttpClient.request<ApiEnvelope<TData> | ''>({
      url: path,
      method,
      data,
      headers,
    });
  } catch (error) {
    if (method === 'GET') {
      const cached = readCachedApiEnvelope<TData>(path);
      if (cached) return cached;
    }

    if (offlineMetadata && method !== 'GET' && shouldQueueOfflineMutation(path, method, data)) {
      queueOfflineMutation({
        path,
        method,
        body: data,
        ...offlineMetadata,
      });
      throw new ApiError(0, OFFLINE_QUEUED_ERROR);
    }

    if (method !== 'GET' && !isAuthEndpoint(path) && !isQueueableBody(data)) {
      throw new ApiError(0, OFFLINE_UNSUPPORTED_ERROR);
    }

    throw error;
  }

  if (response.status === 401 && !isRetry && !isAuthEndpoint(path)) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      return sendEnvelopeRequest<TData>(
        path,
        method,
        data,
        buildHeaders(path, method, offlineMetadata),
        true,
        offlineMetadata
      );
    }

    setAccessToken(null);
    onSessionExpired?.();
  }

  const envelope = normalizeEnvelope<TData>(response);

  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(
      response.status,
      envelope && !envelope.success ? envelope.error : NO_SERVER_RESPONSE_ERROR
    );
  }

  if (!envelope || !envelope.success) {
    // A 2xx status with no parseable body, or (defensively) a body that
    // claims success: false alongside a 2xx status — either way this is
    // a malformed response, not a legitimate success, so it must never
    // be handed back to the caller as if it were.
    throw new ApiError(response.status, NO_SERVER_RESPONSE_ERROR);
  }

  if (method === 'GET') {
    writeCachedApiEnvelope(path, envelope);
  }

  return envelope;
}

export async function apiRequest<TData>(
  path: string,
  options: RequestOptions = {}
): Promise<ApiSuccessEnvelope<TData>> {
  const method = options.method ?? 'GET';
  const offlineMetadata = createOfflineRequestMetadata(path, method, options.body);

  return sendEnvelopeRequest<TData>(
    path,
    method,
    options.body,
    buildHeaders(path, method, offlineMetadata),
    false,
    offlineMetadata
  );
}

/**
 * Multipart file upload (guest identification documents, etc.). Always
 * issues a physical POST — the backend's update routes are PATCH, but
 * PHP never populates `$_FILES` for a PATCH/PUT request body, multipart
 * or not, so a file-bearing update must travel as POST with a `_method:
 * 'PATCH'` field in the FormData (Laravel's documented method-spoofing
 * mechanism) rather than as a real HTTP PATCH. Callers needing that
 * append the field themselves before calling this.
 */
export async function apiUpload<TData>(
  path: string,
  formData: FormData
): Promise<ApiSuccessEnvelope<TData>> {
  return sendEnvelopeRequest<TData>(path, 'POST', formData, buildUploadHeaders());
}

/**
 * Fetches a binary response (e.g. a streamed guest ID document) as a
 * Blob, attaching the same in-memory bearer token and the same
 * single-retry-on-401 behavior as apiRequest/apiUpload — but with no
 * JSON envelope to normalize, since the backend streams raw bytes for
 * these routes rather than wrapping them.
 */
export async function apiDownloadFile(path: string, isRetry = false): Promise<Blob> {
  const response = await activeHttpClient.request<Blob>({
    url: path,
    method: 'GET',
    responseType: 'blob',
    headers: buildUploadHeaders(),
  });

  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      return apiDownloadFile(path, true);
    }

    setAccessToken(null);
    onSessionExpired?.();
  }

  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(response.status, NO_SERVER_RESPONSE_ERROR);
  }

  return response.data;
}

function buildHeaders(
  path?: string,
  method?: NonNullable<RequestOptions['method']>,
  offlineMetadata: OfflineRequestMetadata | null = null
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Lets the backend localize receipts, emails, and validation messages to
    // match the UI language.
    'Accept-Language': currentApiLanguage(),
  };

  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const propertyId = getSelectedPropertyId();
  if (propertyId) {
    headers['X-Property-Id'] = String(propertyId);
  }

  if (offlineMetadata) {
    headers['X-Client-Operation-Id'] = offlineMetadata.clientOperationId;

    if (
      path
      && method
      && ['PUT', 'PATCH', 'DELETE'].includes(method)
      && offlineMetadata.lastKnownUpdatedAt
    ) {
      headers['X-Resource-Updated-At'] = offlineMetadata.lastKnownUpdatedAt;
    }
  }

  return headers;
}

/**
 * Deliberately omits Content-Type — letting axios/the browser set the
 * `multipart/form-data; boundary=...` header itself is required for a
 * FormData body to parse on the server at all (see the http instance's
 * own docblock above).
 */
function buildUploadHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const propertyId = getSelectedPropertyId();
  if (propertyId) {
    headers['X-Property-Id'] = String(propertyId);
  }

  return headers;
}

function shouldQueueOfflineMutation(
  path: string,
  method: NonNullable<RequestOptions['method']>,
  data?: unknown
): boolean {
  return method !== 'GET'
    && !isAuthEndpoint(path)
    && !path.startsWith('/offline-sync-operations')
    && isQueueableBody(data);
}

function isQueueableBody(data: unknown): boolean {
  return !(
    (typeof FormData !== 'undefined' && data instanceof FormData)
    || (typeof Blob !== 'undefined' && data instanceof Blob)
    || (typeof File !== 'undefined' && data instanceof File)
  );
}

function createOfflineRequestMetadata(
  path: string,
  method: NonNullable<RequestOptions['method']>,
  data: unknown
): OfflineRequestMetadata | null {
  if (!shouldQueueOfflineMutation(path, method, data)) {
    return null;
  }

  return {
    clientOperationId: createClientOperationId(),
    resource: inferResourceFromPath(path),
    lastKnownUpdatedAt: ['PUT', 'PATCH', 'DELETE'].includes(method)
      ? findCachedResourceUpdatedAt(path)
      : null,
  };
}

function isAuthEndpoint(path: string): boolean {
  return [
    '/auth/login',
    '/auth/refresh',
    '/auth/register/email-otp',
    '/auth/register/email-otp/verify',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/forgot-password/resend',
    '/auth/reset-password',
    '/auth/email/verification-notification',
    '/auth/verify-email',
  ].some((endpoint) => path.startsWith(endpoint));
}

function normalizeEnvelope<TData>(
  response: AxiosResponse<ApiEnvelope<TData> | ''>
): ApiEnvelope<TData> | null {
  if (response.status === 204) {
    return { success: true, data: null as TData, meta: null };
  }

  if (!response.data || typeof response.data === 'string') {
    return null;
  }

  return response.data;
}

/**
 * Tracks an in-flight /auth/refresh so concurrent callers share it (see
 * refreshAccessToken).
 */
let inFlightRefresh: Promise<boolean> | null = null;
let inFlightOfflineFlush: Promise<number> | null = null;

/**
 * Single-flight wrapper around performRefresh: while a refresh is in
 * progress, every other 401 that needs one awaits the SAME call instead
 * of firing its own.
 *
 * This is essential because the refresh token is single-use and rotated
 * server-side (App\Http\Controllers\Api\V1\AuthController::refresh deletes
 * the presented token and issues a new one). Two concurrent refreshes —
 * e.g. React StrictMode double-invoking AuthContext's initial /auth/me on
 * a reload, or several parallel queries all 401-ing on page load — would
 * otherwise both present the same cookie; the first rotates it and the
 * second gets a 422 "invalid or expired", which would tear down an
 * otherwise-valid session and log the user out on every refresh.
 */
function refreshAccessToken(): Promise<boolean> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

/**
 * Calls /auth/refresh directly with Axios (never through `apiRequest`,
 * to avoid recursing back into 401 handling) relying on the browser
 * sending the HttpOnly refresh-token cookie automatically via
 * `withCredentials` — the SPA never reads or stores that cookie's value
 * itself (see docs/IMPLEMENTATION_PLAN.md section 5.1).
 */
async function performRefresh(): Promise<boolean> {
  try {
    const response = await activeHttpClient.request<ApiEnvelope<{ access_token: string }> | ''>({
      url: '/auth/refresh',
      method: 'POST',
      headers: { Accept: 'application/json' },
    });

    if (response.status < 200 || response.status >= 300) {
      return false;
    }

    const envelope = normalizeEnvelope<{ access_token: string }>(response);

    if (!envelope?.success || !envelope.data?.access_token) {
      return false;
    }

    setAccessToken(envelope.data.access_token);
    return true;
  } catch {
    return false;
  }
}

export function flushOfflineMutations(): Promise<number> {
  if (inFlightOfflineFlush) {
    return inFlightOfflineFlush;
  }

  inFlightOfflineFlush = performOfflineFlush().finally(() => {
    inFlightOfflineFlush = null;
  });

  return inFlightOfflineFlush;
}

async function performOfflineFlush(): Promise<number> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0;

  let flushed = 0;
  const now = Date.now();

  for (const mutation of readOfflineQueue()) {
    if (
      mutation.status === 'conflicted'
      || mutation.status === 'syncing'
      || (mutation.nextRetryAt && Date.parse(mutation.nextRetryAt) > now)
    ) {
      continue;
    }

    markOfflineMutationSyncing(mutation.id);

    try {
      const response = await sendQueuedMutation(mutation);

      if (response.status < 200 || response.status >= 300) {
        const message = responseErrorMessage(response);

        if (response.status === 401) {
          setAccessToken(null);
          onSessionExpired?.();
          markOfflineMutationRetry(mutation.id, 'Sign in again to sync this queued change.');
          break;
        }

        if (response.status === 409) {
          markOfflineMutationConflicted(mutation.id, message);
          continue;
        }

        if (response.status >= 500 || response.status === 0) {
          markOfflineMutationRetry(mutation.id, message);
          break;
        }

        markOfflineMutationFailed(mutation.id, message);
        continue;
      }

      removeOfflineMutation(mutation.id);
      flushed += 1;
    } catch (error) {
      markOfflineMutationRetry(mutation.id, error instanceof Error ? error.message : 'The sync attempt failed.');
      break;
    }
  }

  return flushed;
}

async function sendQueuedMutation(mutation: OfflineMutation): Promise<AxiosResponse<ApiEnvelope<unknown> | ''>> {
  let response = await activeHttpClient.request<ApiEnvelope<unknown> | ''>({
    url: mutation.path,
    method: mutation.method,
    data: mutation.body,
    headers: {
      ...buildHeaders(mutation.path, mutation.method, {
        clientOperationId: mutation.clientOperationId,
        resource: mutation.resource,
        lastKnownUpdatedAt: mutation.lastKnownUpdatedAt,
      }),
      ...(mutation.propertyId ? { 'X-Property-Id': String(mutation.propertyId) } : {}),
    },
  });

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      response = await activeHttpClient.request<ApiEnvelope<unknown> | ''>({
        url: mutation.path,
        method: mutation.method,
        data: mutation.body,
        headers: {
          ...buildHeaders(mutation.path, mutation.method, {
            clientOperationId: mutation.clientOperationId,
            resource: mutation.resource,
            lastKnownUpdatedAt: mutation.lastKnownUpdatedAt,
          }),
          ...(mutation.propertyId ? { 'X-Property-Id': String(mutation.propertyId) } : {}),
        },
      });
    }
  }

  return response;
}

function responseErrorMessage(response: AxiosResponse<ApiEnvelope<unknown> | ''>): string {
  const envelope = normalizeEnvelope<unknown>(response);

  if (envelope && !envelope.success) {
    return envelope.error.message;
  }

  return `The sync attempt failed with HTTP ${response.status}.`;
}
